<script lang="ts">
	//Services
	import { format } from 'svelte-i18n';
	import { postReq } from '../../../services/http';
	//Stores
	import { user_data, user_settings } from 'src/stores/account/user';
import { app } from 'src/stores/session/native-bridge';
	//Design system components
	import {
		ButtonV2,
		InputV2,
		Alert,
		AlertV2,
		Stack,
		Text,
		toast,
		ToggleV2,
		type ToastElement,
	} from '@Shelly/shelly-design-system';
	import { BiometryService } from 'src/services/biometry/biometry-service';
	import { get } from 'svelte/store';
	import { capFirstChar } from 'src/helpers/utilities';

	let pinValue = $user_data.lock_pin;
	let error = '';
	let biometricToggleValue = BiometryService.isBiometryTurnedOnByUser();
	let isBiometryAvailable = BiometryService.isBiometryAvailable();
	let biometricModalTranslations = {
		title: $format('biometrics_turn_on_title', { default: 'Biometric Auth' }),
		subtle: '',
		description: $format('biometrics_turn_on_desc', {
			default: 'Turn on biometric authentication when pin is required.',
		}),
		cancel: $format('button_cancel', { default: 'Cancel' }),
	};

	$: if (typeof pinValue === 'object' || pinValue?.length < 4 || pinValue?.length > 4) {
		error = $format('user_pin_lock_limit_err');
	} else {
		error = '';
	}

	export const handleUserPin = async (pin) => {
		const res = await postReq('/user/set_user_pin', {
			pin: pin,
		});

		user_data.update({ lock_pin: pin });

		if (res.isok) {
			toast.open({
				type: 'success',
				title: $format('user_pin_lock_scc_toast'),
			});
		} else {
			toast.open({ type: 'danger', title: $format('user_pin_lock_err_toast') });
		}
	};

	const handle_input = (e) => {
		if (isNaN(Number(e.data))) {
			//this must remove all letters from value, need design system solution
			pinValue = pinValue
				.toString()
				.replace(/[^0-9]/g, '')
				.replace(/(\..*?)\..*/g, '$1');

			if (typeof pinValue === 'number' && pinValue === 0) {
				pinValue = '0';
			}
		}
	};

	const toggleBiometrics = async (nextValue: boolean) => {
		biometricToggleValue = nextValue;

		const successToast = {
			type: 'success',
			title: $format('biometric_auth_toggle_success_toast', {
				default: 'Biometric authentication setting updated successfully.',
			}),
		} as ToastElement;
		const errorToast = {
			type: 'danger',
			title: $format('biometric_auth_toggle_error_toast', {
				default: 'Biometric authentication failed',
			}),
			duration: 4000,
		} as ToastElement;

		if (!BiometryService.isBiometryAvailable()) {
			toast.open(errorToast);
			setTimeout(() => {
				biometricToggleValue = !biometricToggleValue;
			}, 100);
			return;
		}

		const res = await BiometryService.startBiometricAuthentication(
			biometricModalTranslations.title,
			biometricModalTranslations.subtle,
			biometricModalTranslations.description,
			biometricModalTranslations.cancel,
			'',
		);

		if (res?.error === 'Canceled' || res.error === 'userCancel') {
			return;
		}

		if (!res.success) {
			toast.open(errorToast);
			biometricToggleValue = !biometricToggleValue;
			return;
		}

		const updateSettingResponse = await BiometryService.toggleBiometrySetting(biometricToggleValue);

		if (updateSettingResponse.isok) {
			const userSettings = get(user_settings);

			userSettings.biometrics.enabled = biometricToggleValue ? '1' : '0';
			user_settings.set(userSettings);
			toast.open(successToast);
		}
	};
</script>

<Stack direction="column" directionMobile="row" align="between" alignMobile="none" gap="m">
	<div>
		<Text styleType="body" class="mb-s">
			{capFirstChar($format('lock_pin', { default: 'Lock Pin' }))}
		</Text>
		<Text styleType="body-small" class="text-color-muted">
			{$format('app_settings_lock_pin_description', {
				default: 'Set up your pin',
			})}
		</Text>

		<div class="width-md-l width-sm-full mt-s">
			<InputV2
				backgroundColor="color-black"
				disabled={$user_data?.installer}
				{error}
				icon={'fas fa-key'}
				bind:value={pinValue}
				label={typeof $user_data.lock_pin === 'number' && $user_data.lock_pin !== 0
					? $user_data.lock_pin
					: $format('placeholder_set_pin')}
				type={'password'}
				size="m"
				on:input={handle_input}
				on:keydown={handle_input}
				id="input-mbe3tk5"
				aria-labelledby="input-mbe3tk5"
			/>
		</div>
	</div>

	<ButtonV2
		size="m"
		variant="primary"
		class="width-md-auto width-sm-full align-self-end"
		disabled={!!error}
		on:click={() => handleUserPin(pinValue)}>{$format('button_save')}</ButtonV2
	>
</Stack>
{#if $app}
	{#if !isBiometryAvailable}
		<Alert
			type="warning"
			heading={$format('biometry_warning_not_avaliable', {
				default:
					'Biometric authentication is not available on this device. Check your device settings to enroll.',
			})}
			class="mt-s"
		/>
	{/if}
	<div class="d-flex justify-content-between align-items-center gap-m mt-s">
		<div class="min-width-0">
			<Text styleType="body" class="mb-s">
				{capFirstChar(
					$format('enable_biometric_auth', {
						default: 'Enable Biometric Authentication',
					}),
				)}
			</Text>
			<Text styleType="body-small" class="text-color-muted">
				{$format('biometrics_turn_on_desc', {
					default: 'Turn on biometric authentication when pin is required.',
				})}
			</Text>
		</div>

		<ToggleV2
			checked={biometricToggleValue}
			disabled={!isBiometryAvailable}
			size="m"
			on:click={() => toggleBiometrics(!biometricToggleValue)}
			id="toggle-fjalfgi"
			aria-labelledby="toggle-fjalfgi"
		/>
	</div>
{/if}
